import React from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shield, LayoutDashboard, QrCode, Siren, Users, Car,
  Search, MessageCircle, ShieldAlert, Sun, Moon, LogOut, ChevronRight, Radio
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useLang } from "@/context/LangContext";
import LangToggle from "@/components/LangToggle";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const RESPONDER_ROLES = ["volunteer", "police", "hospital", "ambulance", "ngo", "admin", "super_admin"];
const ADMIN_ROLES = ["admin", "super_admin"];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { t } = useLang();
  const nav = useNavigate();

  const NAV = [
    { to: "/app", label: t("side_overview"), icon: LayoutDashboard, end: true },
    { to: "/app/qr", label: t("side_qr"), icon: QrCode },
    { to: "/app/sos", label: t("side_sos"), icon: Siren, danger: true },
    { to: "/app/family", label: t("side_family"), icon: Users },
    { to: "/app/vehicle", label: t("side_vehicle"), icon: Car },
    { to: "/app/lost-found", label: t("side_lostfound"), icon: Search },
    { to: "/app/community", label: t("side_community"), icon: ShieldAlert },
    { to: "/app/ai", label: t("side_ai"), icon: MessageCircle },
  ];

  const initials = (user?.name || "U").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const isResponder = RESPONDER_ROLES.includes(user?.role);

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden lg:flex w-72 flex-col border-r border-border/70 bg-card/40 backdrop-blur-xl sticky top-0 h-screen">
        <Link to="/" data-testid="sidebar-brand" className="flex items-center gap-2 px-6 h-16 border-b border-border/60">
          <div className="w-9 h-9 rounded-xl bg-[hsl(var(--primary))] grid place-items-center">
            <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="font-display font-black tracking-tight text-lg">Guardian360</div>
        </Link>
        <nav className="p-4 space-y-1 flex-1 overflow-auto">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.to.replace(/\//g, '-')}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors
                 ${isActive
                   ? "bg-[hsl(var(--primary))] text-white"
                   : "text-foreground/75 hover:bg-muted hover:text-foreground"
                 } ${item.danger && !isActive ? "hover:text-[hsl(var(--destructive))]" : ""}`
              }
            >
              <item.icon className="w-4 h-4" />
              <span className="flex-1">{item.label}</span>
              <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-60 transition-opacity" />
            </NavLink>
          ))}
          {isResponder && (
            <NavLink
              to="/portal/responder"
              data-testid="nav-responder"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium mt-4 border ${
                  isActive
                    ? "bg-[hsl(var(--destructive))] text-white border-[hsl(var(--destructive))]"
                    : "text-foreground/80 hover:bg-muted border-[hsl(var(--destructive))]/40"
                }`
              }
            >
              <Radio className="w-4 h-4" /> {t("side_responder")}
            </NavLink>
          )}
          {isAdmin && (
            <NavLink
              to="/app/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium mt-2 border border-dashed border-border ${
                  isActive ? "bg-foreground text-background" : "text-foreground/75 hover:bg-muted"
                }`
              }
            >
              <Shield className="w-4 h-4" /> {t("side_admin")}
            </NavLink>
          )}
        </nav>
        <div className="p-4 border-t border-border/60">
          <div className="flex items-center gap-3 mb-3">
            <Avatar className="w-9 h-9">
              <AvatarFallback className="bg-[hsl(var(--primary))] text-white text-xs">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.role}</div>
            </div>
            <LangToggle />
          </div>
          <div className="flex gap-2">
            <Button data-testid="theme-toggle" variant="outline" size="sm" onClick={toggle} className="flex-1">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              <span className="ml-2 hidden xl:inline">{theme === "dark" ? t("side_light") : t("side_dark")}</span>
            </Button>
            <Button data-testid="logout-btn" variant="ghost" size="sm" onClick={() => { logout(); nav("/"); }} className="flex-1">
              <LogOut className="w-4 h-4 mr-2" /> {t("side_signout")}
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <MobileTopBar />
        <motion.main
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="flex-1 p-4 lg:p-8"
        >
          {children}
        </motion.main>
      </div>
    </div>
  );
}

function MobileTopBar() {
  const { theme, toggle } = useTheme();
  return (
    <header className="lg:hidden sticky top-0 z-40 glass-nav h-14 px-4 flex items-center justify-between">
      <Link to="/app" className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-[hsl(var(--primary))] grid place-items-center">
          <Shield className="w-4 h-4 text-white" />
        </div>
        <span className="font-display font-bold">Guardian360</span>
      </Link>
      <div className="flex items-center gap-1">
        <LangToggle />
        <Button data-testid="theme-toggle-mobile" variant="ghost" size="sm" onClick={toggle}>
          {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </Button>
      </div>
    </header>
  );
}
